import { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { isNavItemActive } from "./utils"
import { useCredits } from "../../context/credits"
import { getUnreadCount } from "../../services/notification-service"
import { openCookiePreferences } from "../cookieBanner"

type NavigationComponentProps = {
    onNavigate?: () => void
    mobile?: boolean
}

export const NavigationComponent = ({
                                        onNavigate,
                                        mobile = false,
                                    }: NavigationComponentProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { balance: creditBalance, lowBalance, refresh: refreshCredits } = useCredits()
    const [unreadCount, setUnreadCount] = useState(0)

    // Reîmprospătează soldul la fiecare navigare, ca badge-ul să reflecte cheltuielile/achizițiile
    // făcute pe pagina anterioară fără un store global desincronizat.
    useEffect(() => {
        refreshCredits()
    }, [location.pathname, refreshCredits])

    useEffect(() => {
        const fetchUnread = () => {
            getUnreadCount()
                .then((r) => setUnreadCount(r.count))
                .catch(() => {})
        }
        fetchUnread()
        const interval = setInterval(fetchUnread, 30000)
        return () => clearInterval(interval)
    }, [])

    const handleNavigate = (path: string) => {
        navigate(`/${path}`)
        onNavigate?.()
    }

    const logout = () => {
        localStorage.removeItem("jwtToken")
        navigate("/")
        onNavigate?.()
    }

    const itemClass = (id: string) =>
        `navigation-item flex items-center rounded-2xl px-4 py-4 text-left ${
            isNavItemActive(location.pathname, id) ? "navigation-active-item" : ""
        }`

    return (
        <div
            id="navigation"
            className={`flex h-full min-h-screen w-full flex-col bg-white ${
                mobile ? "px-5 py-5" : "px-4 py-4 sm:px-6 sm:py-6"
            }`}
        >
            <div className="flex items-center justify-between">
                <img
                    className={
                        mobile
                            ? "w-[130px]"
                            : "mt-2 mb-6 w-[130px] sm:mt-4 sm:mb-10 sm:w-[160px]"
                    }
                    src="/images/jobbin-logo-with-icon.png"
                    alt="Jobbin"
                />

                {mobile && onNavigate ? (
                    <button
                        type="button"
                        onClick={onNavigate}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-figmaLightBg"
                        aria-label="Close menu"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-figmaBlack"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                ) : null}
            </div>

            <div className={`flex flex-col gap-2 sm:gap-3 ${mobile ? "mt-8" : ""}`}>
                <button
                    type="button"
                    className={itemClass("home")}
                    id="home"
                    onClick={() => handleNavigate("home")}
                >
                    <img
                        src="/images/navigation-home.svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        alt="Acasă"
                    />
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px]">
                        Acasă
                    </p>
                </button>

                <button
                    type="button"
                    className={itemClass("candidates")}
                    id="candidates"
                    onClick={() => handleNavigate("candidates")}
                >
                    <img
                        src="/images/navigation-star.svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        alt="Candidați"
                    />
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px]">
                        Candidați
                    </p>
                </button>

                <button
                    type="button"
                    className={itemClass("jobs")}
                    id="jobs"
                    onClick={() => handleNavigate("jobs")}
                >
                    <img
                        src="/images/navigation-chat.svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        alt="Joburi"
                    />
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px]">
                        Joburi
                    </p>
                </button>

                <button
                    type="button"
                    className={itemClass("events")}
                    id="events"
                    onClick={() => handleNavigate("events")}
                >
                    <img
                        src="/images/navigation-calendar.svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        alt="Evenimente"
                    />
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px]">
                        Evenimente
                    </p>
                </button>

                <button
                    type="button"
                    className={itemClass("notifications")}
                    id="notifications"
                    onClick={() => handleNavigate("notifications")}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.8}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                    </svg>
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px] flex-1">
                        Notificări
                    </p>
                    {unreadCount > 0 && (
                        <span className="ml-2 rounded-full bg-figmaContrast px-2 py-0.5 text-[11px] font-bold text-white">
                            {unreadCount}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    className={itemClass("credits")}
                    id="credits"
                    onClick={() => handleNavigate("credits")}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="navigation-img mr-3 shrink-0">
                        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                        <text x="10" y="14" textAnchor="middle" fontSize="10" fontWeight="700" fill="currentColor">C</text>
                    </svg>
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px] flex-1">
                        Portofel
                    </p>
                    {creditBalance !== null && (
                        <span
                            className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${
                                lowBalance ? "bg-amber-500" : "bg-figmaPrimary"
                            }`}
                            title={lowBalance ? "Sold scăzut — reîncarcă" : undefined}
                        >
                            {creditBalance}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    className={itemClass("settings")}
                    id="settings"
                    onClick={() => handleNavigate("settings")}
                >
                    <img
                        src="/images/navigation-settings.svg"
                        className="navigation-img mr-3 h-5 w-5 shrink-0"
                        alt="Setări"
                    />
                    <p className="text-[15px] font-bold text-figmaBlack sm:text-[16px]">
                        Setări
                    </p>
                </button>

                <button
                    type="button"
                    onClick={logout}
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-white p-2 shadow-lg shadow-gray-500/20 transition-all hover:scale-105 hover:shadow-xl sm:h-14 sm:w-14 ml-4 mt-2"
                    aria-label="Logout"
                >
                    <img
                        src="/images/logout-btn.png"
                        className="h-full w-full object-contain"
                        alt="Logout"
                    />
                </button>
            </div>

            <div className="mt-auto pt-4 border-t border-gray-100">
                <div className="text-[11px] text-figmaDarkGray leading-relaxed space-y-0.5">
                    <p className="font-bold text-figmaBlack">GETJOBBIN S.R.L.</p>
                    {/* firma nu este inregistrata in scopuri de TVA, deci CUI-ul se scrie fara prefixul RO */}
                    <p>CUI: 46505834</p>
                    <p>Reg. Com.: J2022002086051</p>
                    <p>Str. Sovata, Nr. 13, Bl. PB3, Ap. 13, 410298 Oradea, Bihor</p>
                    <p>contact@jobbin.ro &middot; 0771 596 762</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
                    <a
                        href="https://jobbin.ro/termeni.html"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        Termeni și Condiții
                    </a>
                    <a
                        href="https://jobbin.ro/confidentialitate.html"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        Confidențialitate
                    </a>
                    <a
                        href="https://jobbin.ro/cookies.html"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        Cookie-uri
                    </a>
                    <a
                        href="https://jobbin.ro/gdpr.html"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        Drepturile tale
                    </a>
                    <a
                        href="https://anpc.ro/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        ANPC
                    </a>
                    {/* art. 7 alin. (3) GDPR: retragerea acordului trebuie sa fie la fel de simpla ca acordarea */}
                    <button
                        type="button"
                        onClick={openCookiePreferences}
                        className="text-[11px] text-figmaPrimary underline"
                    >
                        Preferințe cookie-uri
                    </button>
                </div>
            </div>
        </div>
    )
}
